import { Search, Filter, User } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserCard } from "@/components/user/UserCard";
import { CreateUserDialog } from "@/components/user/UserCreateDialog";
import { Sidebar } from "@/components/Sidebar";

interface User {
  id: number;
  username: string;
  email: string;
  collaborator: "BROKER" | "ADMIN";
  blocked: boolean;
}

export function UserControl() {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("name");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const route = import.meta.env.VITE_URL_ENDPOINT;

  const isAdmin = true;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${route}/api/users`);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      const formattedUsers = data.map((user: User) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        collaborator: user.collaborator,
        blocked: user.blocked || false,
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      (filterType === "name" &&
        user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (filterType === "email" &&
        user.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole =
      roleFilter === "all" || user.collaborator === roleFilter;

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && !user.blocked) ||
      (statusFilter === "blocked" && user.blocked);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const activeUsers = users.filter((user) => !user.blocked).length;
  const blockedUsers = users.filter((user) => user.blocked).length;
  const adminUsers = users.filter(
    (user) => user.collaborator === "ADMIN",
  ).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isAdmin={isAdmin}
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex flex-1 flex-col lg:ml-0">
          {/* Top Header */}
          <header className="border-b border-gray-200 bg-white shadow-sm">
            <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 lg:hidden"
                >
                  <i className="fas fa-bars text-xl"></i>
                </button>
                <h1 className="ml-4 text-xl font-semibold text-gray-900 sm:text-2xl lg:ml-0">
                  Gerenciamento de Usuários
                </h1>
              </div>
            </div>
          </header>

          {/* Stats Cards */}
          <div className="bg-white p-4 shadow-sm sm:p-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <div className="rounded-lg bg-gradient-to-r from-gray-600 to-gray-700 p-4 text-white sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <i className="fas fa-users text-xl sm:text-2xl"></i>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs font-medium text-gray-100 sm:text-sm">
                      Total de Usuários
                    </p>
                    <p className="text-2xl font-bold sm:text-3xl">
                      {users.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-r from-green-600 to-green-700 p-4 text-white sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <i className="fas fa-user-check text-xl sm:text-2xl"></i>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs font-medium text-green-100 sm:text-sm">
                      Usuários Ativos
                    </p>
                    <p className="text-2xl font-bold sm:text-3xl">
                      {activeUsers}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-r from-red-600 to-red-700 p-4 text-white sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <i className="fas fa-user-times text-xl sm:text-2xl"></i>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs font-medium text-red-100 sm:text-sm">
                      Usuários Bloqueados
                    </p>
                    <p className="text-2xl font-bold sm:text-3xl">
                      {blockedUsers}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <i className="fas fa-user-shield text-xl sm:text-2xl"></i>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs font-medium text-blue-100 sm:text-sm">
                      Administradores
                    </p>
                    <p className="text-2xl font-bold sm:text-3xl">
                      {adminUsers}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6 border-gray-200/50 bg-white/90 p-4 shadow-xl backdrop-blur-md lg:p-6">
            <div className="flex flex-col gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 pl-10 focus:border-blue-500 focus:ring-blue-500"
                  placeholder={`Buscar por ${filterType === "name" ? "nome" : "email"}...`}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200">
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Filtrar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Nome</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200">
                    <SelectValue placeholder="Cargo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Cargos</SelectItem>
                    <SelectItem value="ADMIN">Administrador</SelectItem>
                    <SelectItem value="BROKER">Corretor</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Status</SelectItem>
                    <SelectItem value="active">Ativos</SelectItem>
                    <SelectItem value="blocked">Bloqueados</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <CreateUserDialog onUserCreate={fetchUsers} />
            </div>
          </div>

          {/* Users List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4 px-32">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <UserCard
                    key={user.id}
                    id={user.id}
                    name={user.username}
                    email={user.email}
                    role={user.collaborator}
                    blocked={user.blocked}
                    onUserUpdate={fetchUsers}
                  />
                ))
              ) : (
                <div className="px-5 py-12 text-center">
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    Nenhum usuário encontrado
                  </h3>
                  <p className="text-gray-600">
                    Tente ajustar os filtros ou criar um novo usuário.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
